import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, TimeoutError, catchError, filter, identity, takeUntil, throwError, timeout } from 'rxjs';

/** Set to false for requests that must survive a route change, e.g. hub negotiates started by app-wide services. */
export const CANCEL_ON_NAVIGATION = new HttpContextToken<boolean>(() => true);

@Injectable({
  providedIn: 'root'
})
export class ApiRequestInterceptorService implements HttpInterceptor {

  constructor(
    private messageService: ToastrService,
    private router: Router) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const routeChangedObservable = this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    );

    return next.handle(req).pipe(
      timeout(150000),
      req.context.get(CANCEL_ON_NAVIGATION) ? takeUntil(routeChangedObservable) : identity,
      catchError(error => {
        // Ignore cancelled/aborted requests (happens during navigation)
        if (error.name === 'AbortError' || error.name === 'CanceledError' || error.status === 0) {
          return new Observable<never>();
        }

        const hideErrorsStatusCodes = req.params.getAll('hideErrorsStatusCodes')?.map(code => +code);

        if (hideErrorsStatusCodes && hideErrorsStatusCodes.includes(error.status)) {
          return this.onSuppressedError(error);
        }

        if (req.url.includes('VinSights') &&
          error.status === 404) {
          return new Observable<never>();
        }

        if (req.url.includes('PhoneNumberValidationResults') &&
          error.status === 404) {
          return new Observable<never>();
        }

        const errorMessage = this.parseErrorMessage(error);

        if (error.status == HttpStatusCode.UnprocessableEntity && errorMessage.includes("Could not convert string to DateTime")) {
          return new Observable<never>();
        }

        this.messageService.error(errorMessage, '', { enableHtml: true });
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /** Called when a response error matches a hideErrorsStatusCodes entry.
   *  Returns an Observable that replaces the error in the stream.
   *  Default: silently swallow (return a never-completing Observable).
   *  Override in subclasses to change the behaviour per-app. */
  protected onSuppressedError(_error: any): Observable<never> {
    return new Observable<never>();
  }

  private parseErrorMessage(error: any): string {
    let errorMessage = 'There was an unexpected problem with your request.';

    if (error instanceof TimeoutError) {
      errorMessage = 'The request has timed out.';
    } else if (error.error?.errors?.length > 0) {
      errorMessage = '<ul>';
      for (const e of error.error.errors) {
        if (e.userFriendlyMessage) {
          errorMessage += '<li>' + e.userFriendlyMessage + '</li>';
        } else if (e.message) {
          errorMessage += '<li>' + e.message + '</li>';
        } else {
          errorMessage += `<li>${e}</li>`;
        }
      }
      errorMessage += '</ul>';
    } else if (error.error?.detail) {
      errorMessage = error.error.detail;
    } else if (error.status === 404) {
      errorMessage = 'The resource you have requested does not exist.';
    } else if (error.status === 403) {
      errorMessage = 'You do not have access to this resource.';
    } else if (error.status === 401) {
      errorMessage = 'Unauthorized user.';
    }

    return errorMessage;
  }
}
